import { isZyVersionNodeVisible, normalizeSelectedZyVersionFilter, type SelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReplaceStep } from '@tiptap/pm/transform';

export interface ZyVersionDeletePluginOptions {
    onHiddenZyVersionDeleted: (node: PMNode) => void;
    getVersionFilter?: () => SelectedZyVersionFilter;
}

const ZY_VERSION_TYPES = new Set(['zyVersionBlock', 'zyVersionInline']);

function isZyVersionNode(node: PMNode): boolean {
    return ZY_VERSION_TYPES.has(node.type.name);
}

function collectZyVersionsInRange(doc: PMNode, from: number, to: number): PMNode[] {
    const nodes: PMNode[] = [];
    doc.nodesBetween(from, to, node => {
        if (isZyVersionNode(node)) {
            nodes.push(node);
        }
    });
    return nodes;
}

function zyVersionNodePersists(afterDoc: PMNode, node: PMNode): boolean {
    const id = node.attrs?.id;
    if (typeof id !== 'string' || id === '') {
        return true;
    }
    let found = false;
    afterDoc.descendants(n => {
        if (found) {
            return false;
        }
        if (isZyVersionNode(n) && n.attrs?.id === id) {
            found = true;
        }
    });
    return found;
}

/**
 * ProseMirror plugin that invokes a callback whenever a transaction deletes
 * a zyVersion (block or inline) node that is hidden for the current selectedZyVersion.
 */
export function createZyVersionDeletePlugin(options: ZyVersionDeletePluginOptions): Plugin {
    const { onHiddenZyVersionDeleted, getVersionFilter } = options;

    return new Plugin({
        key: new PluginKey('zyVersionDelete'),

        filterTransaction(tr, state) {
            if (!tr.docChanged || tr.steps.length === 0 || !onHiddenZyVersionDeleted || tr.getMeta('y-sync$')) {
                return true;
            }

            if (!tr.steps.some(step => step instanceof ReplaceStep)) {
                return true;
            }

            const selectedZyVersion = getVersionFilter?.() ?? null;
            if (normalizeSelectedZyVersionFilter(selectedZyVersion) === null) {
                return true;
            }

            let doc: PMNode = state.doc;
            for (const step of tr.steps) {
                if (step instanceof ReplaceStep) {
                    const removedCandidates = collectZyVersionsInRange(doc, step.from, step.to);
                    const result = step.apply(doc);
                    if (result.failed || !result.doc) {
                        break;
                    }
                    doc = result.doc;

                    for (const node of removedCandidates) {
                        const version = String(node.attrs.version ?? '');
                        if (isZyVersionNodeVisible(selectedZyVersion, version)) {
                            continue;
                        }
                        const id = node.attrs?.id;
                        if (typeof id !== 'string' || id === '') {
                            continue;
                        }
                        if (!zyVersionNodePersists(doc, node)) {
                            onHiddenZyVersionDeleted(node);
                            return true;
                        }
                    }
                } else {
                    const result = step.apply(doc);
                    if (result.failed || !result.doc) {
                        break;
                    }
                    doc = result.doc;
                }
            }
            return true;
        },
    });
}
